<script setup lang="ts">
/**
 * MY — Allotment of Shares (Otherwise Than Cash, M32202) 문서-뷰 패널.
 *
 * CDD(PreSetCddPanel) 미러링. 단, CDD 가 "문서 유형별 인스턴스" 인 반면 OTC 는
 * "한 task = 12문서 패키지 + 공유 values 맵" 이다. 오른쪽 = 4단계 문서 패키지,
 * 왼쪽 = 챗봇(person lookup). 변수 클릭 → 챗봇/상단 입력바/주소 입력으로 채운다.
 */
import { ref, reactive, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  OTC_DOCS, OTC_DOC_CODES, labelize, typeOf, comboSourceOf, isOptional,
  parseClauseNo, clauseBasisOf, inferAllotteeCategory,
  OTC_ALLOTTEE_MAX, OTC_SHARE_CLASS_MAX, type OtcDocCode,
} from './otc/otcFields';
import { OTC_DOC_CTX } from './otc/otcContext';
import { buildOtcVariables, buildAllottees, emptyOtcState, type OtcState } from '../mappers/allotmentOtcMapper';
import { comboOptions, resolveNationality, resolveOccupation, resolveRace, resolveCountryOption, type ComboOpt } from './otc/otcOptions';
import { formatMalaysiaIdForCdd } from './cdd/cddIdFormat';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { getActiveMembers } from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import AllotmentOtcPackageDoc from './otc/AllotmentOtcPackageDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  pendingPick?: { value: string; seq: number } | null;
  /** studio 챗봇 정관 Clause 선택 결과 (PreSetDocGenView.studioAppliedClause). */
  appliedClause?: { clauseNumber: string; governingBasis: 'constitution' | 'articles' | 'third_schedule' | null; rawOption?: string; seq: number } | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

// ─── Task Title ───────────────────────────────────────────────
const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

// ─── 공유 상태(OtcState) ────────────────────────────────────────
const state = reactive<OtcState>(emptyOtcState());
const allotteeCountRef = computed(() => state.allotteeCount);
const shareClassCountRef = computed(() => state.shareClassCount);
const subscriberTypeRef = computed(() => state.subscriberType);

// ─── 활성 필드 + 입력바 ─────────────────────────────────────────
const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function activate(key: string) {
  activeKey.value = key;
  // 정관 Clause / Article No. → AI 챗봇 clause 선택. 단, entity 등록(잠금)이면 무시.
  if (key === 'constitution_clause_number') {
    if (clauseLocked.value) { activeKey.value = ''; return; }
    emit('request-person-lookup', {
      fieldGroup: 'constitution-clause',
      key: 'constitution_clause_number',
      clauseOptions: clauseOptions.value,
      defaultValue: state.values.constitution_clause_number || '',
      defaultBasis: state.choices.resolution_basis || 'constitution',
    });
    inputKey.value = '';
    return;
  }
  const t = typeOf(key);
  if (t === 'person' || t === 'entity') {
    emit('request-person-lookup', { fieldGroup: 'otc', key, defaultRole: null });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = state.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) state.values[inputKey.value] = inputText.value.trim();
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }
// ROC Cover Letter "Tarikh Dokumen"(tarikh_dokumen) 은 Section 76 Notice 의 Date(signature_date) 와 자동 연동.
//   sync 로 넣은 마지막 값을 기억 → 사용자가 tarikh_dokumen 을 직접 바꾸면(override) 이후 덮어쓰지 않는다.
let lastSyncedDocDate = '';
function setFieldValue(key: string, val: string) {
  state.values[key] = val;
  if (val && invalidKeys.value.has(key)) { invalidKeys.value.delete(key); invalidKeys.value = new Set(invalidKeys.value); }
  // 개별 배정 수량이 바뀌면 Total(total_new_shares) 자동 재계산 → 합계 불일치 방지.
  if (/^allottee_shares_\d+$/.test(key)) recomputeTotalNewShares();
  // Section 76 Date → ROC Tarikh Dokumen 자동 채움(미입력이거나 이전 sync 값 그대로면). 직접입력 override 는 보존.
  if (key === 'signature_date') {
    const cur = String(state.values.tarikh_dokumen || '');
    if (!cur || cur === lastSyncedDocDate) {
      state.values.tarikh_dokumen = val;
      lastSyncedDocDate = val;
      if (invalidKeys.value.has('tarikh_dokumen')) { invalidKeys.value.delete('tarikh_dokumen'); invalidKeys.value = new Set(invalidKeys.value); }
    }
  }
}

/** Total = Σ allottee_shares_n (모든 문서의 Total 이 개별 합과 항상 일치하도록). */
function recomputeTotalNewShares() {
  let sum = 0;
  for (let n = 1; n <= state.allotteeCount; n++) {
    sum += Number(String(state.values[`allottee_shares_${n}`] || '').replace(/[^0-9.]/g, '')) || 0;
  }
  state.values.total_new_shares = sum > 0 ? String(sum) : '';
}

// ── 조건 토글 ──
function flag(name: string): boolean { return !!state.flags[name]; }
function setFlag(name: string, val: boolean) { state.flags[name] = val; }
function toggleFlag(name: string) {
  // 정관 등록 잠금 시 sole/multiple 은 director 수 기반 자동값 유지(수동 토글 무시하지 않되, 결의근거 basis 만 잠금)
  state.flags[name] = !state.flags[name];
}
function choiceOf(name: string): string { return state.choices[name] || ''; }
function setChoice(name: string, val: string) {
  if (name === 'resolution_basis' && clauseLocked.value) return; // 등록된 정관이면 basis 잠금
  state.choices[name] = val;
}

// ── Governing Document (정관 결의 근거) ──
const clauseLocked = ref(false);
const clauseOptions = ref<string[]>([]);
/** clause 옵션/직접입력 → 번호 정규화 저장 + basis 자동 감지 (잠금 시 무시). */
function setClause(raw: string) {
  if (clauseLocked.value) return;
  const s = String(raw || '');
  const basis = clauseBasisOf(s);
  if (basis !== 'other') state.choices.resolution_basis = basis;
  state.values.constitution_clause_number = basis === 'third_schedule' ? '' : parseClauseNo(s);
}

// ── 반복부: Allottees ──
function addAllottee() { if (state.allotteeCount < OTC_ALLOTTEE_MAX) state.allotteeCount += 1; }
function removeAllottee(index: number) {
  const total = state.allotteeCount;
  if (total <= 1) {
    state.values.allottee_name_1 = ''; state.values.allottee_address_1 = '';
    state.values.allottee_shares_1 = ''; state.values.allottee_id_no_1 = '';
    recomputeTotalNewShares();
    return;
  }
  for (let i = index; i < total; i++) {
    state.values[`allottee_name_${i}`] = state.values[`allottee_name_${i + 1}`] || '';
    state.values[`allottee_address_${i}`] = state.values[`allottee_address_${i + 1}`] || '';
    state.values[`allottee_shares_${i}`] = state.values[`allottee_shares_${i + 1}`] || '';
    state.values[`allottee_id_no_${i}`] = state.values[`allottee_id_no_${i + 1}`] || '';
  }
  state.values[`allottee_name_${total}`] = ''; state.values[`allottee_address_${total}`] = '';
  state.values[`allottee_shares_${total}`] = ''; state.values[`allottee_id_no_${total}`] = '';
  state.allotteeCount = total - 1;
  recomputeTotalNewShares();
}

// ── entity 활성 member → Allottee 표 일괄 로드 (name/주소/contactCd/id, 주식수는 사용자 입력) ──
const canLoadMembers = computed(() => !!entityCd.value);
const loadingMembers = ref(false);
async function loadAllotteesFromMembers() {
  if (!entityCd.value || loadingMembers.value) return;
  loadingMembers.value = true;
  progress.value = 'Loading members…';
  try {
    const res: any = await getActiveMembers(entityCd.value);
    const members: any[] = res?.data?.members || res?.members || [];
    const seen = new Set<string>();
    const memberShares = (m: any): number => {
      const n = Number(m.numberOfShares);
      if (Number.isFinite(n) && n > 0) return n;
      const sum = ['sharesOrdinary', 'sharesPreference', 'sharesOthers']
        .reduce((s, k) => s + (Number(m[k]) || 0), 0);
      return sum > 0 ? sum : 0;
    };
    const rows = members
      .map((m: any) => ({
        name: String(m.name || m.contactName || [m.firstName, m.middleName, m.surname].filter(Boolean).join(' ') || '').trim(),
        cc: String(m.contactCd || m.contactCode || m.CONTACT_CD || '').trim(),
        addr: String(m.primaryAddress || m.residentialAddress || m.registeredAddress || m.address || m.addresses?.[0]?.value || '').trim(),
        id: String(m.nricNo || m.contactRegNo || m.regNo || m.identityNo || m.icNo || '').trim(),
        shares: memberShares(m), // 현재 보유주식 수 → Share(s) Allotted 열
        cat: inferAllotteeCategory(m.categoryCd || m.CATEGORY_CD || m.category, m.nricNo || m.contactRegNo || m.regNo || m.identityNo || m.icNo), // 개인/법인 자동판정(Subscription 문서 타입)
        // Subscription 문서용 세부필드 — 멤버 데이터에 있으면 채움(없으면 빈칸)
        occ: resolveOccupation(m.occupation || m.businessOccupation || m.Occupation || ''),
        dob: String(m.dateOfBirth || m.DATE_OF_BIRTH || m.dob || '').split(/[ T]/)[0],
        nat: resolveNationality(String(m.nationalityName || m.nationality || m.nationalityCd || '')),
        race: resolveRace(String(m.race || m.raceName || '')), // 콤보 옵션(대문자)으로 정규화 — "Chinese" → "CHINESE"
      }))
      .filter((r) => {
        if (!r.name) return false;
        if (r.cc) { if (seen.has(r.cc)) return false; seen.add(r.cc); }
        return true;
      })
      .slice(0, OTC_ALLOTTEE_MAX);
    if (!rows.length) { progress.value = '⚠ No active members found for this entity.'; return; }
    // Allottee 표를 멤버로 세팅 (기존 행 값 초기화 후 채움 — 공유 state 라 전 문서 동기화)
    for (let n = 1; n <= Math.max(state.allotteeCount, rows.length); n++) {
      ['name', 'address', 'shares', 'id_no', 'contact_cd', 'category'].forEach((f) => { delete state.values[`allottee_${f}_${n}`]; });
      ['occupation', 'dob', 'nationality', 'race'].forEach((f) => { delete state.values[`subscriber_${f}_${n}`]; });
    }
    rows.forEach((r, i) => {
      const n = i + 1;
      state.values[`allottee_name_${n}`] = r.name;
      if (r.addr) state.values[`allottee_address_${n}`] = r.addr;
      if (r.id) state.values[`allottee_id_no_${n}`] = r.id;
      if (r.cc) state.values[`allottee_contact_cd_${n}`] = r.cc;
      if (r.shares > 0) state.values[`allottee_shares_${n}`] = String(r.shares);
      state.values[`allottee_category_${n}`] = r.cat; // Subscription 문서 타입 자동판정
      // Subscription 세부필드 — 멤버 데이터에 있을 때만
      if (r.occ) state.values[`subscriber_occupation_${n}`] = r.occ;
      if (r.dob) state.values[`subscriber_dob_${n}`] = r.dob;
      if (r.nat) state.values[`subscriber_nationality_${n}`] = r.nat;
      if (r.race) state.values[`subscriber_race_${n}`] = r.race;
    });
    state.allotteeCount = Math.max(1, rows.length);
    recomputeTotalNewShares(); // Total = Σ 개별 배정 수량 (로드/편집 항상 일치)
    progress.value = `Loaded ${rows.length} member(s) with their current shares — adjust the allotted amounts as needed.`;
  } catch (e) {
    console.warn('[OTC] load members failed', e);
    progress.value = '⚠ Failed to load members.';
  } finally {
    loadingMembers.value = false;
  }
}

// ── 반복부: Share classes (Section 78) ──
function addShareClass() { if (state.shareClassCount < OTC_SHARE_CLASS_MAX) state.shareClassCount += 1; }
function removeShareClass(index: number) {
  const total = state.shareClassCount;
  if (total <= 1) {
    ['type', 'details', 'number', 'price', 'total'].forEach((f) => { state.values[`share_class_${f}_1`] = ''; });
    return;
  }
  for (let i = index; i < total; i++) {
    ['type', 'details', 'number', 'price', 'total'].forEach((f) => {
      state.values[`share_class_${f}_${i}`] = state.values[`share_class_${f}_${i + 1}`] || '';
    });
  }
  ['type', 'details', 'number', 'price', 'total'].forEach((f) => { state.values[`share_class_${f}_${total}`] = ''; });
  state.shareClassCount = total - 1;
}

function setSubscriberType(t: 'individual' | 'corporate') { state.subscriberType = t; }
// 문서 안 [[subscriber_type]] 는 상단 토글(state.subscriberType)을 그대로 따라감 — 콤보 옵션이 Individual/Corporate 뿐이라 상단이 단일 소스.
watch(() => state.subscriberType, (t) => {
  state.values.subscriber_type = t === 'corporate' ? 'Corporate' : 'Individual';
}, { immediate: true });

// ── [[Signers]] — 문서별 서명자 선택 ──
function signersOf(varKey: string): any[] { return state.signers[varKey] || []; }
function setSigners(varKey: string, list: any[]) { state.signers[varKey] = Array.isArray(list) ? list : []; }

function comboOptionsOf(key: string): ComboOpt[] { return comboOptions(comboSourceOf(key)); }

// ─── 레지스터 write 용 shareholders 배열 (M32200 과 동일 포맷) ───
//   각 allottee(contactCd 있는 것만) → entity_my/shares(event_type='Allot')로 등록.
//   비현금(OTC)이므로 sharesPayable = B06002(Otherwise in Cash).
const numOnly = (v: any) => Number(String(v ?? '').replace(/[^0-9.]/g, '')) || 0;
function buildShareholdersForRegister(): any[] {
  const price = numOnly(state.values.share_price);
  const rows: any[] = [];
  for (let n = 1; n <= state.allotteeCount; n++) {
    const contactCd = String(state.values[`allottee_contact_cd_${n}`] || '').trim();
    const name = String(state.values[`allottee_name_${n}`] || '').trim();
    const shares = numOnly(state.values[`allottee_shares_${n}`]);
    if (!contactCd || shares <= 0) continue;
    const amount = price > 0 ? Math.round(shares * price * 100) / 100 : 0;
    rows.push({
      shareholder: contactCd,
      shareholderName: name,
      currencies: [{
        currency: 'B08114', // MYR
        shareClasses: [{
          classOfShares: 'Ordinary',
          numberOfShares: shares,
          issuedShareCapital: amount,
          paidUpShareCapital: amount,
          sharesPayable: 'B06002', // Otherwise in Cash (OTC)
        }],
      }],
    });
  }
  return rows;
}

// ── Section 78 (TMP084) 표 자동계산 — 앞서 입력한 배정 수량/주가에서 파생(읽기전용 표시) ──
//   · Details of Shares Allotted primary(1행): Number = Σ 배정수량, Price = 주가, Total = Number × Price.
//   · Summary of Shares Issued: 주식 수 기준 · OTC 는 전량 "Otherwise Than Cash"(현금=0).
//     - Current Allotment = 이번 배정 수량, Additional = 0, Accumulated = Current + Additional.
function recomputeSection78() {
  const total = numOnly(state.values.total_new_shares);
  const price = numOnly(state.values.share_price);
  const val = Math.round(total * price * 100) / 100;
  const S = (n: number) => (n > 0 ? String(n) : '');
  // Details of Shares Allotted — primary(1행) 클래스 자동
  if (!String(state.values.share_class_type_1 || '').trim()) state.values.share_class_type_1 = 'Ordinary';
  state.values.share_class_number_1 = S(total);
  state.values.share_class_price_1 = String(state.values.share_price || '').trim() ? String(price) : '';
  state.values.share_class_total_1 = S(val);
  // Summary of Shares Issued (주식 수 기준, OTC = 전량 비현금)
  state.values.total_current_cash = '0';
  state.values.total_additional_cash = '0';
  state.values.total_accumulated_cash = '0';
  state.values.total_current_otherwise = S(total);
  state.values.total_additional_otherwise = '0';
  state.values.total_accumulated_otherwise = S(total);
  state.values.total_current = S(total);
  state.values.total_additional = '0';
  state.values.total_accumulated = S(total);
}
watch(() => [state.values.total_new_shares, state.values.share_price], recomputeSection78, { immediate: true });

// ── pick-place ──
const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value) return false;
  state.values[key] = pendingPickValue.value;
  pendingPickValue.value = '';
  return true;
}

// ─── 주소 입력 ─────────────────────────────────────────────────
const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string }>({});
function openAddress(key: string) {
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: state.values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  // OTC 주소는 단일 라인 → 전체를 한 줄로 결합. (모달/직접입력 = 컴포넌트로 조합 — officer 경로처럼 백엔드 조합값이 없음)
  const composed = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
    .map((s) => String(s || '').trim()).filter(Boolean).join(', ');
  // 말레이시아 주소는 전부 대문자 (백엔드 formatAddressString 규칙과 동일).
  state.values[key] = /malaysia/i.test(String(a.country || '')) ? composed.toUpperCase() : composed;
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

// studio 챗봇 정관 Clause 선택 결과 → constitution_clause_number + resolution_basis 반영 (등록 잠금이면 무시)
watch(() => props.appliedClause, (a) => {
  if (!a || clauseLocked.value) return;
  const detected = clauseBasisOf(a.rawOption || '');
  const basis = a.governingBasis || (detected !== 'other' ? detected : null) || 'constitution';
  state.choices.resolution_basis = basis;
  state.values.constitution_clause_number = basis === 'third_schedule' ? '' : (a.clauseNumber || '');
});

// ─── 사람 상세 → 필드 채움 (챗봇 선택) ───────────────────────────
function applyPersonToField(key: string, a: any) {
  const name = a.name || a.contactName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') || '';
  state.values[key] = name;

  const id = formatMalaysiaIdForCdd(a.nricNo || a.passportNo || a.contactRegNo || '');
  // 주소는 백엔드가 이미 조합·정규화(말레이시아=대문자 등)한 값을 그대로 사용 — 프론트 재파싱 금지(케이스/포맷 변형 방지).
  //   officer 상세: addresses[] (label 'Residential'/'Service') → residential 우선, 없으면 첫 주소. 폴백으로 residential/primaryAddress.
  const backendAddrs = Array.isArray(a.addresses) ? a.addresses : [];
  const residentialVal = backendAddrs.find((x: any) => /residential/i.test(String(x?.label || '')))?.value;
  const addrLine = String(residentialVal || backendAddrs[0]?.value || a.residentialAddress || a.primaryAddress || '').trim();
  const nat = resolveNationality(String(a.nationalityName || '')) || resolveNationality(String(a.nationality || ''));
  const occ = resolveOccupation(a.occupation || a.businessOccupation || '');
  const phone = [a.areaCode, a.mobileNo].filter(Boolean).join(' ').trim() || a.mobileNo || '';
  const dob = (a.dateOfBirth || '').toString().split(/[ T]/)[0];

  // 클릭한 필드의 prefix 로 형제 필드 채움
  const m = key.match(/^(.*?)(?:_full_name|_name)(_\d+)?$/);
  const prefix = m ? m[1] : '';
  const idx = m && m[2] ? m[2] : '';
  const put = (k: string, val: string) => { if (val) state.values[k] = val; };

  if (prefix === 'subscriber') {
    put('subscriber_id', id); put('subscriber_address', addrLine);
    put('subscriber_dob', dob); put('subscriber_nationality', nat); put('subscriber_occupation', occ);
  } else if (prefix === 'allottee') {
    put(`allottee_id_no${idx}`, id); put(`allottee_address${idx}`, addrLine);
    // 레지스터 write 용 — allottee 의 contact 코드 보관 (person lookup 으로 채워진 경우만)
    const cc = a.contactCd || a.contactCD || a.CONTACT_CD || '';
    if (cc) state.values[`allottee_contact_cd${idx}`] = String(cc);
    // 개인/법인 자동판정(Subscription 문서 타입) — contact category 우선, 없으면 ID 추론
    state.values[`allottee_category${idx}`] = inferAllotteeCategory(a.categoryCd || a.CATEGORY_CD || a.category, id);
    // Subscription 문서용 per-allottee 세부필드 자동채움
    put(`subscriber_occupation${idx}`, occ); put(`subscriber_dob${idx}`, dob);
    put(`subscriber_nationality${idx}`, nat); put(`subscriber_race${idx}`, resolveRace(String(a.race || a.raceCd || '')));
  } else if (prefix === 'director') {
    put('director_nric', id);
  } else if (prefix === 'lodger') {
    put('lodger_nric', id); put('lodger_address', addrLine); put('lodger_phone', phone); put('lodger_email', a.email || a.emailAddress || '');
  } else if (prefix === 'secretary' || prefix === 'cosec') {
    put(`${prefix}_address`, addrLine); if (prefix === 'cosec') put('cosec_phone', phone);
  } else if (prefix === 'bo') {
    // Beneficial Owner — 엔티티 BO 레코드(/entity_my/beneficial-owner)에 이미 있는 데이터로 채움.
    put(`bo_registration_no${idx}`, id);
    // 주소 — Residential → Registered, Business → Business (BO 레코드에 둘 다 존재)
    const boAddrs = Array.isArray(a.addresses) ? a.addresses : [];
    const resAddr = String(boAddrs.find((x: any) => /residential/i.test(x?.label || ''))?.value || a.residentialAddress || addrLine || '').trim();
    const bizAddr = String(boAddrs.find((x: any) => /business/i.test(x?.label || ''))?.value || resAddr).trim();
    put(`bo_registered_address${idx}`, resAddr);
    put(`bo_business_address${idx}`, bizAddr);
    // Nature of Control ← 엔티티 BO 기준(Criteria A~F, %), 없으면 BO 유형(Direct/Indirect). Place ← 국적 국가
    put(`bo_nature${idx}`, String(a.criteriaText || a.boTypeDescription || a.boType || a.typeOfBo || ''));
    const boPlace = resolveCountryOption(String(a.nationalityName || a.nationality || '')) || String(a.nationalityName || a.nationality || '');
    put(`bo_place${idx}`, boPlace);
  }
}
watch(() => props.selectedPerson, (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'otc' || !ctx.key) return;
  applyPersonToField(ctx.key, p as any);
});

provide(OTC_DOC_CTX, {
  valueOf: (k) => state.values[k] || '',
  labelOf: labelize, typeOf, activeKey, activate, setFieldValue, openAddress,
  comboOptionsOf,
  flag, setFlag, toggleFlag, choiceOf, setChoice,
  clauseLocked, clauseOptions, setClause,
  allotteeCount: allotteeCountRef, addAllottee, removeAllottee,
  loadAllotteesFromMembers, canLoadMembers, loadingMembers,
  shareClassCount: shareClassCountRef, addShareClass, removeShareClass,
  subscriberType: subscriberTypeRef, setSubscriberType,
  entityCd, signersOf, setSigners,
  isInvalid,
  pendingPickValue, placePick,
});

// ─── Entity 선택 → 회사 필드 + Company Secretary(cosec) 자동 채움 ───
//   props.entityData(=formData.tlinkData) 즉시값 + fetchEntityAddresses(정관/주소) + 첫 비서 → cosec/secretary.
//   비어있는 필드만 채움(사용자 편집 보존). M32200 패턴.
const entityPrefilled = ref('');
watch(entityCd, async (cd) => {
  if (!cd || cd === entityPrefilled.value) return;
  entityPrefilled.value = cd;
  const put = (k: string, v: any) => {
    const s = String(v ?? '').trim();
    if (s && !String(state.values[k] || '').trim()) state.values[k] = s;
  };
  // 1) tlinkData 즉시값
  put('company_name', props.entityData?.companyName || props.entityData?.entityName);
  put('company_registration_no', props.entityData?.registrationNo);
  // 2) 정관 clause + 등록주소 + 회사명/등록번호/구번호 보강
  try {
    const gov = await fetchEntityAddresses(cd);
    if (gov) {
      put('company_name', gov.companyName);
      put('company_registration_no', gov.regNo);
      put('old_registration_no', gov.regNoOld);          // 구 등록번호 (있으면 헤더에 표시)
      put('company_address', gov.registered);
      // 등록된 정관(clause/article) 있으면 → clause + resolution_basis 자동 바인딩 + 잠금(수정 불가)
      const regClause = String(gov.clauseNo || gov.articleNo || '').trim();
      if (regClause) {
        const basis = clauseBasisOf(regClause);
        state.choices.resolution_basis = basis === 'other' ? 'constitution' : basis;
        state.values.constitution_clause_number = basis === 'third_schedule' ? '' : parseClauseNo(regClause);
        clauseLocked.value = true;
      } else {
        clauseLocked.value = false;
      }
    }
  } catch (e) { console.warn('[OTC] entity address fetch failed', e); }
  // 2b) 미등록 정관 → AI 선택용 clause 옵션 로드
  try {
    const r = await fetch('/api/entity_my/profile/resolution-options', { credentials: 'include' }).then((x) => x.json());
    const opts = r?.articleNoOptions || r?.data?.resolution?.articleNoOptions || r?.resolution?.articleNoOptions || [];
    clauseOptions.value = Array.isArray(opts)
      ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
      : [];
  } catch (e) { console.warn('[OTC] resolution-options fetch failed', e); }
  // 2c) 이사 수 → sole / multiple 자동 (1명=sole, 초과=multiple)
  try {
    const dr = await fetch(`/api/documents/variables/directors/${encodeURIComponent(cd)}`, { credentials: 'include' }).then((x) => x.json());
    const directors: any[] = dr?.data?.directors || dr?.directors || [];
    const active = directors.filter((d) => !String(d.cessationDate || d.CESSATION_DATE || d.dateOfCessation || '').trim());
    const n = active.length || directors.length;
    if (n > 0) state.flags.is_multiple_director_company = n > 1;
  } catch (e) { console.warn('[OTC] director count fetch failed', e); }
  // 2d) 기존 member(주주) 수 → single / multiple member 문구 자동 (Waiver "I/We … member(s)", Authority members' resolution)
  try {
    const mr: any = await getActiveMembers(cd);
    const members: any[] = mr?.data?.members || mr?.members || [];
    const seen = new Set<string>();
    const distinct = members.filter((m) => {
      const name = String(m.name || m.contactName || [m.firstName, m.middleName, m.surname].filter(Boolean).join(' ') || '').trim();
      if (!name) return false;
      const cc = String(m.contactCd || m.contactCode || m.CONTACT_CD || '').trim();
      if (cc) { if (seen.has(cc)) return false; seen.add(cc); }
      return true;
    });
    if (distinct.length > 0) state.flags.is_multiple_member = distinct.length > 1;
  } catch (e) { console.warn('[OTC] member count fetch failed', e); }
  // Allottee 표는 자동 프리필하지 않음 — 사용자가 "Load from members" 버튼으로 직접 로드.
  // 3) Company Secretary → cosec_* (Letter/Authority/ROC) + secretary_* (S76 Secretary)
  try {
    const secRes: any = await docGenApi.getSecretaries(cd);
    const secs = secRes?.data?.secretaries || secRes?.data || [];
    if (Array.isArray(secs) && secs.length > 0) {
      const s = secs[0];
      const secCc = String(s.contactCd || s.contactCode || s.CONTACT_CD || '').trim();
      let det: any = s;
      if (secCc) {
        try { const r: any = await docGenApi.getContactDetail(cd, secCc); det = r?.data || r || s; } catch { /* noop */ }
      }
      const name = det.name || det.contactName || s.name || '';
      const reg = det.contactRegNo || det.licenseNumber || s.contactRegNo || '';
      const phone = [det.areaCode, det.mobileNo].filter(Boolean).join(' ').trim() || det.mobileNo || s.mobileNo || '';
      const addrs = Array.isArray(det.addresses) ? det.addresses : [];
      const addr = addrs[0]?.composed || addrs[0]?.value || det.primaryAddress || det.residentialAddress || '';
      put('cosec_name', name); put('cosec_registration_no', reg); put('cosec_phone', phone); put('cosec_address', addr);
      put('secretary_name', name); put('secretary_license_no', det.licenseNumber || reg);
    }
  } catch (e) { console.warn('[OTC] getSecretaries failed', e); }
}, { immediate: true });

// ─── 진행률(%) — 활성 문서 union key ───────────────────
//   Subscription(TMP081/082)은 allottee 별로 별도 생성하므로 일반 문서 목록에서 제외.
const activeDocCodes = computed<OtcDocCode[]>(() =>
  OTC_DOC_CODES.filter((c) => c !== 'TMP081' && c !== 'TMP082'),
);
const overallProgress = computed(() => {
  const keys = new Set<string>();
  for (const c of activeDocCodes.value) OTC_DOCS[c].keys.forEach((k) => { if (!isOptional(k)) keys.add(k); });
  const list = [...keys];
  if (!list.length) return 0;
  const filled = list.filter((k) => String(state.values[k] || '').trim()).length;
  return Math.round((filled / list.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

// ─── Create / Update Task ──────────────────────────────────────
const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  const code = props.selection?.task?.TASK_CODE;
  if (code) { try { const cfg = await getTaskConfigByCode(code); taskConfigSteps.value = (cfg as any)?.processSteps || []; } catch { /* noop */ } }
});

// 각 문서의 [[Signers]] 서명영역 → OtcSigners var-key (OtcSigners 의 var-key 와 일치). '' = 서명영역 없음.
const SIGNER_KEY_BY_DOC: Record<OtcDocCode, string> = {
  TMP080: 'waiver',        // Waiver of Preemption
  TMP091: 'authority_dcr', // Authority to Issue (DCR board)
  TMP088: 'pricing',       // Pricing (board)
  TMP081: 'subscription',  // Subscription — Individual
  TMP082: 'subscription',  // Subscription — Corporate
  TMP089: 'letter',        // Letter of Allotment
  TMP090: 'increase',      // Increase of Paid-Up Capital (board)
  TMP086: 's76_director',  // Section 76 — Director
  TMP085: '',              // Section 76 — Secretary (표 서명, [[Signers]] 없음)
  TMP083: 'statement',     // Statement (OTC)
  TMP084: '',              // Section 78 (director 직접 서명)
  TMP087: '',              // ROC Cover (서명영역 없음)
};

// docCode → 실제 tmpId (selection.documents 에서 docCode 매칭, 폴백 templateName)
const availableTemplates = computed(() => (props.selection?.documents || []).filter((d: any) => d.tmpId));
function resolveTmpId(docCode: OtcDocCode): number | null {
  const docs = availableTemplates.value as any[];
  const byCode = docs.find((d) => String(d.docCode || '').toUpperCase() === docCode);
  if (byCode?.tmpId) return byCode.tmpId;
  return null;
}

/** allottee 개인/법인 자동판정 — 저장 category 우선, 없으면 ID 추론. */
function allotteeCategoryOf(n: number): 'individual' | 'corporate' {
  const stored = String(state.values[`allottee_category_${n}`] || '').toLowerCase();
  if (stored === 'corporate' || stored === 'individual') return stored as 'individual' | 'corporate';
  return inferAllotteeCategory('', state.values[`allottee_id_no_${n}`]);
}
/** Subscription — Stage 1 allottee 1명당 1문서(individual→TMP081, corporate→TMP082). 각 문서 formData 는 해당 allottee 데이터로 subscriber_* 오버레이. */
function buildSubscriptionDocs(base: Record<string, any>) {
  const tmp081 = resolveTmpId('TMP081');
  const tmp082 = resolveTmpId('TMP082');
  const v = state.values;
  const docs: any[] = [];
  for (let n = 1; n <= state.allotteeCount; n++) {
    const name = String(v[`allottee_name_${n}`] || '').trim();
    if (!name) continue;
    const cat = allotteeCategoryOf(n);
    const tmpId = cat === 'corporate' ? tmp082 : tmp081;
    if (!tmpId) continue;
    const shares = String(v[`allottee_shares_${n}`] || '');
    const signers = state.signers[`subscription_${n}`] || [];
    docs.push({
      templateId: tmpId,
      label: `Subscription — ${name}`,
      personName: name,
      entityCd: entityCd.value || undefined,
      formData: {
        ...base,
        subscriber_name: v[`allottee_name_${n}`] || '',
        subscriber_id: v[`allottee_id_no_${n}`] || '',
        subscriber_address: v[`allottee_address_${n}`] || '',
        subscriber_type: cat === 'corporate' ? 'Corporate' : 'Individual',
        subscriber_occupation: v[`subscriber_occupation_${n}`] || '',
        subscriber_dob: v[`subscriber_dob_${n}`] || '',
        subscriber_nationality: v[`subscriber_nationality_${n}`] || '',
        subscriber_race: v[`subscriber_race_${n}`] || '',
        shares_subscribed: shares, shares_offered: shares, shares_accepted: shares,
        politician_details: v[`politician_details_${n}`] || '',
        pep_connection_details: v[`pep_connection_details_${n}`] || '',
        govt_position_details: v[`govt_position_details_${n}`] || '',
        bo_full_name: v[`bo_full_name_${n}`] || '', bo_nature: v[`bo_nature_${n}`] || '',
        bo_place: v[`bo_place_${n}`] || '', bo_registration_no: v[`bo_registration_no_${n}`] || '',
        bo_registered_address: v[`bo_registered_address_${n}`] || '', bo_business_address: v[`bo_business_address_${n}`] || '',
        is_politician: !!state.flags[`is_politician_${n}`],
        is_pep_connected: !!state.flags[`is_pep_connected_${n}`],
        holds_govt_position: !!state.flags[`holds_govt_position_${n}`],
        is_trustee: !!state.flags[`is_trustee_${n}`],
        confirm_accurate: !!state.flags[`confirm_accurate_${n}`],
        confirm_clean: !!state.flags[`confirm_clean_${n}`],
        confirm_consent: !!state.flags[`confirm_consent_${n}`],
        is_corporate_subscriber: cat === 'corporate',
        is_individual_subscriber: cat === 'individual',
      },
      signers: signers.length ? signers : undefined,
    });
  }
  return docs;
}

// ─── 검증 — 미입력 필드 빨간 하이라이트 + Create Task 시 확인 다이얼로그(차단X, 안내) ───
const invalidKeys = ref<Set<string>>(new Set());
function isInvalid(key: string): boolean { return invalidKeys.value.has(key); }
const confirmMissing = ref(false);
interface MissingItem { label: string; key: string }
const missingList = ref<MissingItem[]>([]);
/** 권장 입력값 검증 → 미입력 항목(라벨+대표 필드키) 목록 + invalidKeys 갱신. */
function validateMissing(): MissingItem[] {
  const bad = new Set<string>();
  const errs: MissingItem[] = [];
  const filled = (k: string) => !!String(state.values[k] || '').trim();
  const need = (k: string, label: string) => { if (!filled(k)) { bad.add(k); errs.push({ label, key: k }); } };
  if (!effectiveTitle.value) errs.push({ label: 'Task Title', key: '' });
  need('company_name', 'Company Name');
  need('share_price', 'Share Price');
  need('allotment_date', 'Allotment Date');
  // 최소 1명의 Allottee (이름 + 주식수)
  let hasAllottee = false;
  for (let n = 1; n <= state.allotteeCount; n++) {
    const nm = String(state.values[`allottee_name_${n}`] || '').trim();
    const sh = Number(String(state.values[`allottee_shares_${n}`] || '').replace(/[^0-9.]/g, '')) || 0;
    if (nm && sh > 0) { hasAllottee = true; break; }
  }
  if (!hasAllottee) { bad.add('allottee_name_1'); bad.add('allottee_shares_1'); errs.push({ label: 'at least one Allottee with No. of Shares', key: 'allottee_name_1' }); }
  invalidKeys.value = bad;
  return errs;
}

// 미입력 항목 클릭 → 해당 필드가 있는 stage 로 전환 + 스크롤 (package doc 이 처리)
const packageDocRef = ref<{ focusField?: (k: string, s: number) => void } | null>(null);
/** 필드가 처음 등장하는 stage (scalar=keys, allottee_*=allottees loop 문서). */
function stageOfField(key: string): number {
  const isAllottee = /^allottee_/.test(key);
  let best = 0;
  for (const c of activeDocCodes.value) {
    const meta = OTC_DOCS[c];
    const hit = isAllottee ? !!meta.loops?.includes('allottees') : meta.keys.includes(key);
    if (hit && (best === 0 || meta.stage < best)) best = meta.stage;
  }
  return best || 1;
}
function goToMissing(item: MissingItem) {
  confirmMissing.value = false;
  if (!item.key) return;
  packageDocRef.value?.focusField?.(item.key, stageOfField(item.key));
}

async function handleCreateTask() {
  if (creating.value) return;
  // 생성 대상 문서 = 청약자 유형에 맞는 문서만 (반대 청약 문서 제외)
  const targetCodes = activeDocCodes.value;
  const resolved = targetCodes.map((c) => ({ c, tmpId: resolveTmpId(c) }));
  const missingTpl = resolved.filter((r) => !r.tmpId);
  if (missingTpl.length) {
    const avail = (availableTemplates.value as any[]).map((x) => x.templateName || x.docCode).join(', ') || '(none)';
    progress.value = `⚠ Template not linked for: ${missingTpl.map((m) => OTC_DOCS[m.c].label).join(', ')} — [${avail}]`;
    return;
  }
  // 미입력 항목 검증 → 있으면 확인 다이얼로그(그래도 진행 가능), 없으면 바로 생성
  const missing = validateMissing();
  if (missing.length) { missingList.value = missing; confirmMissing.value = true; return; }
  await doCreateTask(resolved);
}
/** 다이얼로그 "그래도 진행" — 미입력 있어도 생성 진행. */
function proceedAnyway() {
  confirmMissing.value = false;
  const resolved = activeDocCodes.value.map((c) => ({ c, tmpId: resolveTmpId(c) }));
  void doCreateTask(resolved);
}

async function doCreateTask(resolved: { c: OtcDocCode; tmpId: number | null }[]) {
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  // edit 재저장 전, 저장 문서 SIGNER_JSON seed 가 끝났는지 보장 (미완이면 서명자 빈 채로 재생성돼 날아감).
  if (editMode.value && signersSeedPromise) { try { await signersSeedPromise; } catch { /* noop */ } }
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    // shareholders/transactionDate 는 officerData 최상위 — Register 단계(TaskListPage.isMyAllotment)가
    //   addShareholderForAllotmentTask(formData.shareholders) 로 읽어 entity_my/shares 에 write.
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      shareholders: buildShareholdersForRegister(),
      transactionDate: state.values.allotment_date || '',
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }
    progress.value = 'Preparing documents…';

    // 병합 payload 하나를 모든 대상 템플릿에 전달 (템플릿이 자기 [[변수]]만 치환)
    const formData = buildOtcVariables(state as OtcState);
    // 문서별 [[Signers]] → 각 문서의 서명 영역 varKey 로 선택된 서명자 배열 전달
    const docsToSave = resolved.map(({ c, tmpId }) => {
      const varKey = SIGNER_KEY_BY_DOC[c];
      const signers = varKey ? (state.signers[varKey] || []) : [];
      return {
        templateId: tmpId!,
        label: OTC_DOCS[c].label,
        personName: String(state.values.company_name || 'Company'),
        entityCd: entityCd.value || undefined,
        formData,
        signers: signers.length ? signers : undefined,
      };
    });
    // Subscription — allottee 1명당 1문서(개인/법인 자동판정) 추가
    docsToSave.push(...buildSubscriptionDocs(formData));
    try {
      const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
      }).then((r) => r.json());
      if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
    } catch (err: any) {
      console.warn('[OTC] document save failed:', err);
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[OTC] create task 실패', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

// ─── Edit gather/restore ───────────────────────────────────────
//   Register Detail 프리뷰가 상위로 병합해 읽으므로 allottees 배열 + 대표 스칼라를 함께 저장.
function gatherState() {
  return {
    state: JSON.parse(JSON.stringify(state)),
    allottees: buildAllottees(state as OtcState),
    companyName: state.values.company_name || '',
    companyRegistrationNo: state.values.company_registration_no || '',
    sharePrice: state.values.share_price || '',
    subscriberType: state.subscriberType,
  };
}
function restoreState(s: any) {
  const src = s?.state || s?.presetForm?.state;
  if (src && typeof src === 'object') {
    Object.assign(state, { ...emptyOtcState(), ...src });
    state.values = { ...(src.values || {}) };
    state.flags = { ...emptyOtcState().flags, ...(src.flags || {}) };
    state.choices = { ...emptyOtcState().choices, ...(src.choices || {}) };
    state.signers = { ...(src.signers || {}) };
  }
  // 서명자의 source-of-truth 는 문서별 L_DOC_CUST_SAVE.SIGNER_JSON (presetForm.state.signers 는 비어있는 경우가 많음).
  //   → edit 진입 시 저장된 문서들의 signers 를 varKey 로 매핑해 state.signers 를 채운다(빈 슬롯만).
  if (props.editTaskId) signersSeedPromise = seedSignersFromSavedDocs(Number(props.editTaskId));
}
// seed 완료 promise — Update Task 가 seed 보다 먼저 실행돼 서명자가 빈 채로 재저장(=날아감)되는 것을 방지.
let signersSeedPromise: Promise<void> | null = null;

/** tmpId(숫자) → OtcDocCode 역매핑 (selection.documents 기반). */
function docCodeByTmpId(): Record<number, OtcDocCode> {
  const map: Record<number, OtcDocCode> = {};
  for (const d of (props.selection?.documents || []) as any[]) {
    const code = String(d.docCode || '').toUpperCase();
    if (d.tmpId && code) map[Number(d.tmpId)] = code as OtcDocCode;
  }
  return map;
}
/** 파일명 → 서명영역 varKey 폴백 (selection.documents 가 비어 docCode 매핑이 안 될 때 대비). */
function signerVarKeyByFileName(fileName: string): string {
  const f = String(fileName || '');
  if (/Waiver/i.test(f)) return 'waiver';
  if (/Authority\s+to\s+Issue/i.test(f)) return 'authority_dcr';
  if (/Pricing/i.test(f)) return 'pricing';
  if (/Letter\s+of\s+Allotment/i.test(f)) return 'letter';
  if (/Increase\s+of\s+Paid[-\s]?Up/i.test(f)) return 'increase';
  if (/Section\s*76.*Director/i.test(f)) return 's76_director';
  if (/Statement/i.test(f)) return 'statement';
  return '';
}
/** 문서의 서명자 정본 = SIGNER_JSON(d.signers). 레거시 호환으로 formData.signers/Signers 폴백. */
function extractDocSigners(d: any): any[] | null {
  if (Array.isArray(d?.signers) && d.signers.length) return d.signers;
  const fd = d?.formData;
  const raw = fd && typeof fd === 'object' ? (fd.signers ?? fd.Signers) : null;
  if (raw != null && raw !== '') {
    try {
      const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (Array.isArray(arr) && arr.length) return arr;
    } catch { /* fallthrough */ }
  }
  return null;
}
/** 저장된 문서들의 서명자(formData.signers 우선, SIGNER_JSON 폴백) → state.signers (빈 슬롯만 채움). */
async function seedSignersFromSavedDocs(taskId: number) {
  try {
    const res = await fetch(`/api/aiDocuments/incorporation/load-state/${taskId}`, { credentials: 'include' }).then((r) => r.json());
    const docs: any[] = res?.documents || [];
    if (!docs.length) return;
    const codeMap = docCodeByTmpId();
    for (const d of docs) {
      const signers = extractDocSigners(d);
      if (!signers || !signers.length) continue;
      const code = codeMap[Number(d.templateId)];
      const fileName = String(d.fileName || '');
      let varKey = '';
      if (code === 'TMP081' || code === 'TMP082' || /^Subscription\b/i.test(fileName)) {
        // Subscription — allottee 이름(파일명)으로 인덱스 매칭 → subscription_${n}
        const nm = fileName.replace(/^Subscription\s*[—-]\s*/i, '').replace(/\.docx$/i, '').trim().toUpperCase();
        for (let n = 1; n <= state.allotteeCount; n++) {
          if (String(state.values[`allottee_name_${n}`] || '').trim().toUpperCase() === nm) { varKey = `subscription_${n}`; break; }
        }
      } else {
        varKey = (code ? (SIGNER_KEY_BY_DOC[code] || '') : '') || signerVarKeyByFileName(fileName);
      }
      if (varKey && !(state.signers[varKey]?.length)) state.signers[varKey] = signers;
    }
  } catch (e) {
    console.warn('[OTC] seedSignersFromSavedDocs failed', e);
  }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="otc-panel" :class="{ 'otc-studio': studio }">
    <!-- studio 아닐 때만 자체 헤더 -->
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Allotment of Shares (OTC)</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <!-- Task Title (studio 아닐 때) -->
    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field v-model="titleModel" placeholder="e.g. Allotment of Shares — Tilkoblet" variant="outlined" density="compact" hide-details class="ct-input" />
    </div>

    <!-- Entity (studio 아닐 때) -->
    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <!-- 활성 text/date 입력바 -->
    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <!-- 문서 패키지 (4단계) -->
    <div class="co-doc-body">
      <AllotmentOtcPackageDoc ref="packageDocRef" />
    </div>

    <!-- Footer (studio 아닐 때 또는 오류 시) -->
    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn v-if="!studio" color="primary" class="text-none" :loading="creating" :disabled="creating" :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'" @click="handleCreateTask">{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>

    <AddressInputModal v-model="addrModal" :label="addrLabel" :has-components="false" :initial="addrInitial" @apply="applyAddress" />

    <!-- 미입력 항목 확인 — 차단이 아니라 안내 후 진행 여부 선택 -->
    <v-dialog v-model="confirmMissing" max-width="470">
      <v-card class="otc-confirm">
        <div class="oc-head"><v-icon color="#d97706" size="20">mdi-alert-outline</v-icon><span>Some information is missing</span></div>
        <div class="oc-body">
          <p class="oc-lead">The following {{ missingList.length > 1 ? 'items are' : 'item is' }} not filled in <span class="oc-hint">(click to go there)</span>:</p>
          <ul class="oc-list">
            <li v-for="(m, i) in missingList" :key="i">
              <button v-if="m.key" type="button" class="oc-item" @click="goToMissing(m)">
                {{ m.label }}<v-icon size="13">mdi-arrow-right-thin</v-icon>
              </button>
              <span v-else class="oc-item-static">{{ m.label }}</span>
            </li>
          </ul>
          <p class="oc-q">Do you still want to create the task?</p>
        </div>
        <div class="oc-actions">
          <v-btn variant="text" class="text-none" @click="confirmMissing = false">Cancel</v-btn>
          <v-btn color="primary" class="text-none" prepend-icon="mdi-check" @click="proceedAnyway">Proceed anyway</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.otc-panel { display:flex; flex-direction:column; }
.otc-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.otc-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; position:sticky; top:0; z-index:5; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; align-items:center; gap:8px; }
.co-doc-body { padding:20px; background:#eceff5; }
.co-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:12px; }
.co-progress { font-size:12px; color:#5b6472; max-width:420px; }
.co-progress.err { color:#d64545; font-weight:600; }

/* 미입력 확인 다이얼로그 */
.otc-confirm { padding:20px 22px; border-radius:16px; }
.otc-confirm .oc-head { display:flex; align-items:center; gap:8px; font-size:15px; font-weight:800; color:#1f2937; }
.otc-confirm .oc-body { margin-top:12px; font-size:13px; color:#42506a; }
.otc-confirm .oc-lead { margin:0 0 8px; }
.otc-confirm .oc-hint { color:#8a94a6; font-weight:400; font-size:11.5px; }
.otc-confirm .oc-list { list-style:none; margin:0 0 12px; padding:0; display:flex; flex-direction:column; gap:6px; }
.otc-confirm .oc-item { display:inline-flex; align-items:center; gap:4px; border:1px solid #fbd9a6; background:#fff8ef; color:#b45309; font-weight:700; font-size:12.5px; border-radius:8px; padding:6px 10px; cursor:pointer; width:100%; text-align:left; justify-content:space-between; }
.otc-confirm .oc-item:hover { background:#fdecd3; border-color:#f5b96a; }
.otc-confirm .oc-item-static { display:inline-block; font-weight:700; color:#b45309; padding:6px 2px; }
.otc-confirm .oc-q { margin:0; font-weight:700; color:#1f2937; }
.otc-confirm .oc-actions { display:flex; justify-content:flex-end; gap:8px; margin-top:18px; }
</style>
